import { Editor } from "@monaco-editor/react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import type { ActionInstruction } from "../types";
import { CheckSelect } from "./check-select";

const textFieldSx = {
  "& .MuiInputBase-input": { fontSize: "14px" },
  "& .MuiInputLabel-root": {
    fontSize: "14px",
    "&.Mui-focused": { fontSize: "14px" },
  },
};

const selectSx = {
  fontSize: "14px",
  "& .MuiSelect-select": { fontSize: "14px" },
};

const inputLabelSx = {
  fontSize: "14px",
  "&.Mui-focused": { fontSize: "14px" },
};

const menuItemSx = { fontSize: "14px" };

interface ActionSelectorProps {
  action: ActionInstruction | undefined;
  onActionChange: (field: keyof ActionInstruction, value: unknown) => void;
  appConfigId?: string;
}

export const ActionSelector: React.FC<ActionSelectorProps> = ({
  action,
  onActionChange,
  appConfigId,
}) => {
  const actionType = action?.action;
  const currentValue = action?.value;

  if (!actionType) {
    return null;
  }

  const renderActionFields = () => {
    switch (actionType) {
      case "ai_action":
      case "ai_assertion":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label={actionType === "ai_action" ? "AI Instruction" : "AI Assertion"}
              value={typeof currentValue === "string" ? currentValue : ""}
              onChange={(e) => onActionChange("value", e.target.value)}
              size="small"
              placeholder={
                actionType === "ai_action"
                  ? "Describe what action the AI should take"
                  : "Describe what should be asserted/verified"
              }
              multiline
              minRows={1}
              maxRows={10}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={action?.max_mode ?? false}
                  onChange={(e) => {
                    onActionChange("max_mode", e.target.checked);
                  }}
                />
              }
              label="Max Mode (use extended reasoning)"
            />
          </Stack>
        );

      case "ai_auto_close_popup":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Popup Close Hint (optional)"
              value={typeof currentValue === "string" ? currentValue : ""}
              onChange={(e) => onActionChange("value", e.target.value)}
              size="small"
              placeholder="Reject all cookies, dismiss the newsletter modal"
              helperText="Optional. Natural-language hint for how to close the popup. Leave empty to let the AI decide."
              multiline
              minRows={1}
              maxRows={10}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={action?.max_mode ?? false}
                  onChange={(e) => {
                    onActionChange("max_mode", e.target.checked);
                  }}
                />
              }
              label="Max Mode (use extended reasoning)"
            />
          </Stack>
        );

      case "ai_validate_variable":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Variable Names (comma-separated)"
              value={
                typeof currentValue === "object" && currentValue && "variable_name" in currentValue
                  ? Array.isArray((currentValue as { variable_name: string[] }).variable_name)
                    ? (currentValue as { variable_name: string[] }).variable_name.join(", ")
                    : (currentValue as { variable_name: string }).variable_name
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        variable_name?: string[];
                        variable_validation?: string;
                      })
                    : {};
                const variableNames = e.target.value
                  .split(",")
                  .map((v) => v.trim())
                  .filter((v) => v);
                onActionChange("value", {
                  ...current,
                  variable_name: variableNames,
                });
              }}
              size="small"
              placeholder="user_email, user_name"
              helperText="List of variable names to validate"
            />
            <TextField
              fullWidth
              label="Validation Criteria"
              value={
                typeof currentValue === "object" &&
                currentValue &&
                "variable_validation" in currentValue
                  ? (currentValue as { variable_validation: string }).variable_validation
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        variable_name?: string[];
                        variable_validation?: string;
                      })
                    : {};
                onActionChange("value", {
                  ...current,
                  variable_validation: e.target.value,
                });
              }}
              size="small"
              sx={textFieldSx}
              placeholder="Should be a valid email address"
              multiline
              minRows={1}
              maxRows={10}
              helperText="Describe what makes the variables valid"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={action?.max_mode ?? false}
                  onChange={(e) => {
                    onActionChange("max_mode", e.target.checked);
                  }}
                />
              }
              label="Max Mode (use extended reasoning)"
            />
          </Stack>
        );

      case "blur":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This action doesn't require additional parameters.
          </Typography>
        );

      case "check":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This action doesn't require additional parameters.
          </Typography>
        );

      case "click":
      case "click_download":
      case "dblclick":
      case "tap":
        return (
          <Stack spacing={0}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    typeof currentValue === "object" && currentValue && "force" in currentValue
                      ? (currentValue as { force: boolean }).force
                      : false
                  }
                  onChange={(e) => {
                    const current =
                      typeof currentValue === "object" && currentValue
                        ? (currentValue as { force?: boolean })
                        : {};
                    onActionChange("value", {
                      ...current,
                      force: e.target.checked,
                    });
                  }}
                />
              }
              label="Force click (bypass actionability checks)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    typeof currentValue === "object" &&
                    currentValue &&
                    "wait_new_page" in currentValue
                      ? (currentValue as { wait_new_page: boolean }).wait_new_page
                      : false
                  }
                  onChange={(e) => {
                    const current =
                      typeof currentValue === "object" && currentValue
                        ? (currentValue as { wait_new_page?: boolean })
                        : {};
                    onActionChange("value", {
                      ...current,
                      wait_new_page: e.target.checked,
                    });
                  }}
                />
              }
              label="Use new page URL (_blank links)"
            />
          </Stack>
        );

      case "drag_and_drop":
        return (
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={inputLabelSx}>Drag Method</InputLabel>
              <Select
                value={
                  typeof currentValue === "object" && currentValue
                    ? "target_locator" in currentValue
                      ? "locator"
                      : "x" in currentValue || "y" in currentValue
                        ? "absolute"
                        : "delta_x" in currentValue || "delta_y" in currentValue
                          ? "relative"
                          : "locator"
                    : "locator"
                }
                onChange={(e) => {
                  const method = e.target.value;
                  if (method === "locator") {
                    onActionChange("value", { target_locator: "" });
                  } else if (method === "absolute") {
                    onActionChange("value", { x: 0, y: 0 });
                  } else {
                    onActionChange("value", { delta_x: 0, delta_y: 0 });
                  }
                }}
                label="Drag Method"
                sx={selectSx}
              >
                <MenuItem value="locator" sx={menuItemSx}>
                  Drag to Element (Recommended)
                </MenuItem>
                <MenuItem value="absolute" sx={menuItemSx}>
                  Drag to Absolute Position
                </MenuItem>
                <MenuItem value="relative" sx={menuItemSx}>
                  Drag by Relative Offset
                </MenuItem>
              </Select>
            </FormControl>

            {typeof currentValue === "object" &&
              currentValue &&
              "target_locator" in currentValue && (
                <TextField
                  sx={textFieldSx}
                  fullWidth
                  label="Target Element Selector"
                  value={(currentValue as { target_locator: string }).target_locator}
                  onChange={(e) => {
                    onActionChange("value", { target_locator: e.target.value });
                  }}
                  size="small"
                  placeholder=".drop-zone"
                  helperText="CSS selector of the target element to drop onto"
                />
              )}

            {typeof currentValue === "object" &&
              currentValue &&
              ("x" in currentValue || "y" in currentValue) &&
              !("delta_x" in currentValue) && (
                <Stack spacing={2}>
                  <TextField
                    sx={textFieldSx}
                    fullWidth
                    label="X Position (absolute)"
                    type="number"
                    value={(currentValue as { x?: number; y?: number }).x ?? ""}
                    onChange={(e) => {
                      const current =
                        typeof currentValue === "object" && currentValue
                          ? (currentValue as { x?: number; y?: number })
                          : {};
                      onActionChange("value", {
                        ...current,
                        x: parseInt(e.target.value, 10) || 0,
                      });
                    }}
                    size="small"
                    placeholder="300"
                    helperText="Absolute X coordinate on the page"
                  />
                  <TextField
                    sx={textFieldSx}
                    fullWidth
                    label="Y Position (absolute)"
                    type="number"
                    value={(currentValue as { x?: number; y?: number }).y ?? ""}
                    onChange={(e) => {
                      const current =
                        typeof currentValue === "object" && currentValue
                          ? (currentValue as { x?: number; y?: number })
                          : {};
                      onActionChange("value", {
                        ...current,
                        y: parseInt(e.target.value, 10) || 0,
                      });
                    }}
                    size="small"
                    placeholder="500"
                    helperText="Absolute Y coordinate on the page"
                  />
                </Stack>
              )}

            {typeof currentValue === "object" &&
              currentValue &&
              ("delta_x" in currentValue || "delta_y" in currentValue) && (
                <Stack spacing={2}>
                  <TextField
                    sx={textFieldSx}
                    fullWidth
                    label="X Offset (relative)"
                    type="number"
                    value={(currentValue as { delta_x?: number; delta_y?: number }).delta_x ?? ""}
                    onChange={(e) => {
                      const current =
                        typeof currentValue === "object" && currentValue
                          ? (currentValue as {
                              delta_x?: number;
                              delta_y?: number;
                            })
                          : {};
                      onActionChange("value", {
                        ...current,
                        delta_x: parseInt(e.target.value, 10) || 0,
                      });
                    }}
                    size="small"
                    placeholder="100"
                    helperText="Horizontal offset from source element (positive = right)"
                  />
                  <TextField
                    sx={textFieldSx}
                    fullWidth
                    label="Y Offset (relative)"
                    type="number"
                    value={(currentValue as { delta_x?: number; delta_y?: number }).delta_y ?? ""}
                    onChange={(e) => {
                      const current =
                        typeof currentValue === "object" && currentValue
                          ? (currentValue as {
                              delta_x?: number;
                              delta_y?: number;
                            })
                          : {};
                      onActionChange("value", {
                        ...current,
                        delta_y: parseInt(e.target.value, 10) || 0,
                      });
                    }}
                    size="small"
                    placeholder="200"
                    helperText="Vertical offset from source element (positive = down)"
                  />
                </Stack>
              )}
          </Stack>
        );

      case "evaluate":
      case "evaluate_handle":
        return (
          <Stack spacing={1}>
            <Typography variant="body2" color="textSecondary">
              JavaScript Code
            </Typography>
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Editor
                height="200px"
                defaultLanguage="javascript"
                value={typeof currentValue === "string" ? currentValue : ""}
                onChange={(value) => onActionChange("value", value || "")}
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 12,
                  lineNumbers: "on",
                  wordWrap: "on",
                  wrappingIndent: "same",
                }}
              />
            </Box>
            <Typography variant="caption" color="textSecondary">
              JavaScript function or expression to execute (e.g., "() =&gt; document.title")
            </Typography>
          </Stack>
        );

      case "extract_from_file":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="File Paths (comma-separated)"
              value={
                typeof currentValue === "object" && currentValue && "file_paths" in currentValue
                  ? Array.isArray((currentValue as { file_paths: string[] }).file_paths)
                    ? (currentValue as { file_paths: string[] }).file_paths.join(", ")
                    : (currentValue as { file_paths: string[] }).file_paths
                  : typeof currentValue === "string"
                    ? currentValue
                    : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        file_paths?: string[];
                        extraction_task?: string;
                        variable_name?: string | string[];
                      })
                    : {};
                const filePaths = e.target.value
                  .split(",")
                  .map((p) => p.trim())
                  .filter((p) => p);
                onActionChange("value", {
                  ...current,
                  file_paths: filePaths,
                });
              }}
              size="small"
              placeholder="/path/to/file.pdf, https://example.com/doc.pdf"
              helperText="Multiple file paths or URLs separated by commas"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Extraction Task"
              value={
                typeof currentValue === "object" &&
                currentValue &&
                "extraction_task" in currentValue
                  ? (currentValue as { extraction_task: string }).extraction_task
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        file_paths?: string[];
                        extraction_task?: string;
                        variable_name?: string | string[];
                      })
                    : {};
                onActionChange("value", {
                  ...current,
                  extraction_task: e.target.value,
                });
              }}
              size="small"
              placeholder="Extract all email addresses and phone numbers"
              multiline
              minRows={1}
              maxRows={10}
              helperText="Describe what to extract from the files"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Variable Name (optional)"
              value={
                typeof currentValue === "object" && currentValue && "variable_name" in currentValue
                  ? Array.isArray((currentValue as { variable_name: string[] }).variable_name)
                    ? (currentValue as { variable_name: string[] }).variable_name[0] || ""
                    : (currentValue as { variable_name: string }).variable_name
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        file_paths?: string[];
                        extraction_task?: string;
                        variable_name?: string | string[];
                      })
                    : {};
                onActionChange("value", {
                  ...current,
                  variable_name: e.target.value || undefined,
                });
              }}
              size="small"
              placeholder="extracted_data"
              helperText="Variable name to store extracted data"
            />
          </Stack>
        );

      case "extract_from_page":
        return (
          <Stack spacing={2}>
            <Select
              sx={selectSx}
              fullWidth
              value={
                typeof currentValue === "object" &&
                currentValue &&
                "extraction_type" in currentValue
                  ? (currentValue as { extraction_type: string }).extraction_type
                  : "text"
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        extraction_type?: string;
                      })
                    : {};
                onActionChange("value", {
                  ...current,
                  extraction_type: e.target.value,
                });
              }}
              size="small"
            >
              <MenuItem value="text" sx={menuItemSx}>
                Text Content
              </MenuItem>
              <MenuItem value="attribute" sx={menuItemSx}>
                Attribute Value
              </MenuItem>
              <MenuItem value="property" sx={menuItemSx}>
                Property Value
              </MenuItem>
              <MenuItem value="inner_html" sx={menuItemSx}>
                Inner HTML
              </MenuItem>
              <MenuItem value="inner_text" sx={menuItemSx}>
                Inner Text
              </MenuItem>
            </Select>
            {typeof currentValue === "object" &&
              currentValue &&
              "extraction_type" in currentValue &&
              ((currentValue as { extraction_type: string }).extraction_type === "attribute" ||
                (currentValue as { extraction_type: string }).extraction_type === "property") && (
                <TextField
                  sx={textFieldSx}
                  fullWidth
                  label={
                    (currentValue as { extraction_type: string }).extraction_type === "attribute"
                      ? "Attribute Name"
                      : "Property Name"
                  }
                  value={
                    typeof currentValue === "object" &&
                    currentValue &&
                    "attribute_name" in currentValue
                      ? (currentValue as { attribute_name: string }).attribute_name
                      : ""
                  }
                  onChange={(e) => {
                    const current =
                      typeof currentValue === "object" && currentValue
                        ? (currentValue as {
                            attribute_name?: string;
                          })
                        : {};
                    onActionChange("value", {
                      ...current,
                      attribute_name: e.target.value,
                    });
                  }}
                  size="small"
                  placeholder="href, src, data-custom"
                  helperText={
                    (currentValue as { extraction_type: string }).extraction_type === "attribute"
                      ? "Name of the attribute to extract"
                      : "Name of the property to extract"
                  }
                />
              )}
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Variable Name (optional)"
              value={
                typeof currentValue === "object" && currentValue && "variable_name" in currentValue
                  ? (
                      currentValue as {
                        variable_name: string;
                      }
                    ).variable_name
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        variable_name?: string;
                      })
                    : {};
                onActionChange("value", {
                  ...current,
                  variable_name: e.target.value || undefined,
                });
              }}
              size="small"
              placeholder="page_data"
              helperText="Variable name to store extracted page data (auto-generated if empty)"
            />
          </Stack>
        );

      case "extract_structured_data":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Extraction Task"
              value={
                typeof currentValue === "object" &&
                currentValue &&
                "extraction_task" in currentValue
                  ? (currentValue as { extraction_task: string }).extraction_task
                  : typeof currentValue === "string"
                    ? currentValue
                    : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        extraction_task?: string;
                        extraction_variable_target_name?: string;
                      })
                    : {};
                onActionChange("value", {
                  ...current,
                  extraction_task: e.target.value,
                });
              }}
              size="small"
              placeholder="Extract user profiles with name, email, and avatar URL"
              multiline
              minRows={1}
              maxRows={10}
              helperText="Describe the structure of data to extract"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Variable Name (optional)"
              value={
                typeof currentValue === "object" &&
                currentValue &&
                "extraction_variable_target_name" in currentValue
                  ? (
                      currentValue as {
                        extraction_variable_target_name: string;
                      }
                    ).extraction_variable_target_name
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        extraction_task?: string;
                        extraction_variable_target_name?: string;
                      })
                    : {};
                onActionChange("value", {
                  ...current,
                  extraction_variable_target_name: e.target.value || undefined,
                });
              }}
              size="small"
              placeholder="extracted_users"
              helperText="Variable name to store extracted data (auto-generated if empty)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={action?.max_mode ?? false}
                  onChange={(e) => {
                    onActionChange("max_mode", e.target.checked);
                  }}
                />
              }
              label="Max Mode (use extended reasoning)"
            />
          </Stack>
        );

      case "fill":
      case "type": {
        const isAiBased = action?.value_strategy === "ai_based";
        return (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                sx={{ ...textFieldSx, flex: 1 }}
                fullWidth
                label={isAiBased ? "Semantic Description" : "Text to Input"}
                value={
                  isAiBased
                    ? action?.value_semantic_text || ""
                    : typeof currentValue === "string"
                      ? currentValue
                      : ""
                }
                onChange={(e) => {
                  if (isAiBased) {
                    onActionChange("value_semantic_text", e.target.value);
                  } else {
                    onActionChange("value", e.target.value);
                  }
                }}
                size="small"
                placeholder={
                  isAiBased
                    ? "e.g., tomorrow's date in YYYY-MM-DD format"
                    : "Enter text to type or fill"
                }
                multiline
                minRows={1}
                maxRows={10}
              />
              <Tooltip
                title="When enabled, AI will generate the value at runtime based on your description, the current page state, and available variables"
                arrow
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={isAiBased}
                      onChange={(e) => {
                        // Toggle the strategy - just set the strategy value
                        onActionChange("value_strategy", e.target.checked ? "ai_based" : undefined);
                      }}
                    />
                  }
                  label="AI Value"
                  sx={{ ml: 0, mr: 0 }}
                />
              </Tooltip>
            </Stack>
            {isAiBased && (
              <>
                <TextField
                  sx={textFieldSx}
                  fullWidth
                  label="Variable Name (optional)"
                  value={action?.value_variable_name || ""}
                  onChange={(e) =>
                    onActionChange("value_variable_name", e.target.value || undefined)
                  }
                  size="small"
                  placeholder="e.g., generated_date"
                  helperText="Name for storing the resolved value. Auto-generated if empty."
                />
                <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  AI will resolve this value at runtime based on the page context and available
                  variables. Example: "the user_email variable with '+test' added before @" or
                  "tomorrow in MM/DD/YYYY format"
                </Typography>
              </>
            )}
          </Stack>
        );
      }

      case "get_last_email":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Email Address"
              value={
                typeof currentValue === "object" && currentValue && "email" in currentValue
                  ? (currentValue as { email: string }).email
                  : typeof currentValue === "string"
                    ? currentValue
                    : ""
              }
              onChange={(e) => {
                if (!e.target.value) {
                  onActionChange("value", undefined);
                  return;
                }

                if (typeof currentValue === "object" && currentValue) {
                  onActionChange("value", {
                    ...currentValue,
                    email: e.target.value,
                  });
                } else {
                  onActionChange("value", e.target.value);
                }
              }}
              size="small"
              placeholder="user@example.com"
              helperText="Email address to fetch last email from"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Subject Filter (optional)"
              value={
                typeof currentValue === "object" && currentValue && "subject" in currentValue
                  ? (currentValue as { subject: string }).subject
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? currentValue
                    : typeof currentValue === "string"
                      ? { email: currentValue }
                      : {};
                onActionChange("value", {
                  ...current,
                  subject: e.target.value || undefined,
                });
              }}
              size="small"
              placeholder="Welcome"
              helperText="Optional subject filter"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Auto-Reply Body (optional)"
              value={
                typeof currentValue === "object" && currentValue && "reply_body" in currentValue
                  ? (currentValue as { reply_body: string }).reply_body
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? currentValue
                    : typeof currentValue === "string"
                      ? { email: currentValue }
                      : {};
                onActionChange("value", {
                  ...current,
                  reply_body: e.target.value || undefined,
                });
              }}
              size="small"
              placeholder="Thank you for your email..."
              multiline
              minRows={2}
              maxRows={5}
              helperText="Optional auto-reply message"
            />
          </Stack>
        );

      case "get_task_variable":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Variable Name"
            value={typeof currentValue === "string" ? currentValue : ""}
            onChange={(e) => onActionChange("value", e.target.value)}
            size="small"
            placeholder="variable_name"
          />
        );

      case "get_task_variables":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This action doesn't require additional parameters.
          </Typography>
        );

      case "handle_file_chooser":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="File Path(s)"
            value={
              // Handle both direct string/array and object with file_paths/files
              typeof currentValue === "object" &&
              currentValue &&
              ("file_paths" in currentValue || "files" in currentValue)
                ? Array.isArray(
                    (
                      currentValue as {
                        file_paths?: string[];
                        files?: string[];
                      }
                    ).file_paths ||
                      (
                        currentValue as {
                          file_paths?: string[];
                          files?: string[];
                        }
                      ).files,
                  )
                  ? (
                      (
                        currentValue as {
                          file_paths?: string[];
                          files?: string[];
                        }
                      ).file_paths ||
                      (
                        currentValue as {
                          file_paths?: string[];
                          files?: string[];
                        }
                      ).files ||
                      []
                    ).join(", ")
                  : ""
                : Array.isArray(currentValue)
                  ? currentValue.join(", ")
                  : typeof currentValue === "string"
                    ? currentValue
                    : ""
            }
            onChange={(e) => {
              const paths = e.target.value
                .split(",")
                .map((p) => p.trim())
                .filter((p) => p);

              // For single file, use string directly; for multiple, use object with file_paths
              if (paths.length === 0) {
                onActionChange("value", "");
              } else if (paths.length === 1) {
                onActionChange("value", paths[0]);
              } else {
                onActionChange("value", { file_paths: paths });
              }
            }}
            size="small"
            placeholder="/path/to/file.txt"
            helperText="File path(s) to select when file chooser dialog appears (comma-separated for multiple)"
          />
        );

      case "go_back":
      case "go_forward":
      case "reload":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This navigation action doesn't require additional parameters.
          </Typography>
        );

      case "goto":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="URL"
            value={typeof currentValue === "string" ? currentValue : ""}
            onChange={(e) => onActionChange("value", e.target.value)}
            size="small"
            placeholder="https://example.com"
          />
        );

      case "highlight":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This action doesn't require additional parameters.
          </Typography>
        );

      case "hover":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This action doesn't require additional parameters.
          </Typography>
        );

      case "new_task_variable":
      case "set_task_variable":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Variable Name"
              value={
                typeof currentValue === "object" && currentValue && "name" in currentValue
                  ? (currentValue as { name: string }).name
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        name?: string;
                        value?: unknown;
                      })
                    : {};
                onActionChange("value", {
                  ...current,
                  name: e.target.value,
                });
              }}
              size="small"
              placeholder="variable_name"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Variable Value"
              value={
                typeof currentValue === "object" && currentValue && "value" in currentValue
                  ? typeof (currentValue as { value: unknown }).value === "string"
                    ? (currentValue as { value: string }).value
                    : JSON.stringify((currentValue as { value: unknown }).value)
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        name?: string;
                        value?: unknown;
                      })
                    : {};
                let value: unknown = e.target.value;
                try {
                  value = JSON.parse(e.target.value);
                } catch {
                  // Keep as string
                }
                onActionChange("value", {
                  ...current,
                  value,
                });
              }}
              size="small"
              placeholder="Variable value (JSON or string)"
            />
          </Stack>
        );

      case "not_to_be_checked":
      case "not_to_be_disabled":
      case "not_to_be_editable":
      case "not_to_be_empty":
      case "not_to_be_enabled":
      case "not_to_be_hidden":
      case "not_to_be_visible":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This assertion doesn't require additional parameters.
          </Typography>
        );

      case "not_to_contain_text":
      case "not_to_have_text":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Expected Text"
            value={typeof currentValue === "string" ? currentValue : ""}
            onChange={(e) => onActionChange("value", e.target.value)}
            size="small"
            placeholder="Expected text content"
            multiline
            minRows={1}
            maxRows={5}
          />
        );

      case "not_to_have_value":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Expected Value"
            value={typeof currentValue === "string" ? currentValue : ""}
            onChange={(e) => onActionChange("value", e.target.value)}
            size="small"
            placeholder="Expected input value"
          />
        );

      case "not_to_have_values":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Expected Values (comma-separated)"
            value={
              Array.isArray(currentValue)
                ? currentValue.join(", ")
                : typeof currentValue === "string"
                  ? currentValue
                  : ""
            }
            onChange={(e) => {
              const values = e.target.value
                .split(",")
                .map((v) => v.trim())
                .filter((v) => v);
              onActionChange("value", values.length === 1 ? values[0] : values);
            }}
            size="small"
            placeholder="value1, value2, value3"
            helperText="Multiple values separated by commas"
          />
        );

      case "parse_request":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="URL Part to Match"
              value={
                typeof currentValue === "object" && currentValue && "value" in currentValue
                  ? (currentValue as { value: string }).value
                  : typeof currentValue === "string"
                    ? currentValue
                    : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  value: e.target.value,
                });
              }}
              size="small"
              placeholder="/api/users"
              helperText="URL part to match in requests"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Request Method (optional)"
              value={
                typeof currentValue === "object" && currentValue && "request_method" in currentValue
                  ? (currentValue as { request_method: string }).request_method
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  request_method: e.target.value || undefined,
                });
              }}
              size="small"
              placeholder="GET, POST, PUT, DELETE"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="JSON Path (optional)"
              value={
                typeof currentValue === "object" && currentValue && "json_path" in currentValue
                  ? (currentValue as { json_path: string }).json_path
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  json_path: e.target.value || "$",
                });
              }}
              size="small"
              placeholder="$.response.data"
              helperText="JSONPath to extract from response"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Variable Name (optional)"
              value={
                typeof currentValue === "object" && currentValue && "variable_name" in currentValue
                  ? Array.isArray((currentValue as { variable_name: string[] }).variable_name)
                    ? (currentValue as { variable_name: string[] }).variable_name[0]
                    : (currentValue as { variable_name: string }).variable_name
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  variable_name: e.target.value || undefined,
                });
              }}
              size="small"
              placeholder="parsed_data"
              helperText="Variable name to store parsed data"
            />
          </Stack>
        );

      case "perform_request":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Request URL"
              value={
                typeof currentValue === "object" && currentValue && "request_url" in currentValue
                  ? (currentValue as { request_url: string }).request_url
                  : typeof currentValue === "string"
                    ? currentValue
                    : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  request_url: e.target.value,
                });
              }}
              size="small"
              placeholder="https://api.example.com/users"
              helperText="Full URL for the HTTP request"
            />
            <FormControl fullWidth size="small" sx={textFieldSx}>
              <InputLabel>HTTP Method</InputLabel>
              <Select
                value={
                  typeof currentValue === "object" &&
                  currentValue &&
                  "request_method" in currentValue
                    ? (currentValue as { request_method: string }).request_method
                    : "GET"
                }
                onChange={(e) => {
                  const current =
                    typeof currentValue === "object" && currentValue ? currentValue : {};
                  onActionChange("value", {
                    ...current,
                    request_method: e.target.value,
                  });
                }}
                label="HTTP Method"
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="PATCH">PATCH</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>
            <Stack spacing={1}>
              <Typography variant="body2" color="textSecondary">
                Request Headers (JSON)
              </Typography>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Editor
                  height="100px"
                  defaultLanguage="json"
                  value={
                    typeof currentValue === "object" &&
                    currentValue &&
                    "request_headers" in currentValue
                      ? JSON.stringify(
                          (
                            currentValue as {
                              request_headers: Record<string, string>;
                            }
                          ).request_headers,
                          null,
                          2,
                        )
                      : ""
                  }
                  onChange={(value) => {
                    const current =
                      typeof currentValue === "object" && currentValue ? currentValue : {};
                    if (!value || value.trim() === "") {
                      onActionChange("value", {
                        ...current,
                        request_headers: undefined,
                      });
                      return;
                    }
                    // Store the raw string, validation happens on save
                    try {
                      const headers = JSON.parse(value);
                      onActionChange("value", {
                        ...current,
                        request_headers: headers,
                      });
                    } catch {
                      // Keep typing, don't update until valid JSON
                    }
                  }}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 12,
                    lineNumbers: "on",
                    wordWrap: "on",
                    wrappingIndent: "same",
                  }}
                />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Optional HTTP headers as JSON (e.g., {"{"}Authorization: "Bearer token"{"}"})
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="body2" color="textSecondary">
                Request Body (JSON)
              </Typography>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Editor
                  height="150px"
                  defaultLanguage="json"
                  value={
                    typeof currentValue === "object" &&
                    currentValue &&
                    "request_body" in currentValue
                      ? (currentValue as { request_body: string }).request_body
                      : ""
                  }
                  onChange={(value) => {
                    const current =
                      typeof currentValue === "object" && currentValue ? currentValue : {};
                    onActionChange("value", {
                      ...current,
                      request_body: value || undefined,
                    });
                  }}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 12,
                    lineNumbers: "on",
                    wordWrap: "on",
                    wrappingIndent: "same",
                  }}
                />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Optional request body as JSON (e.g., {"{"}name: "John", email: "john@example.com"
                {"}"})
              </Typography>
            </Stack>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Expected Status Code"
              type="number"
              value={
                typeof currentValue === "object" &&
                currentValue &&
                "request_expected_status" in currentValue
                  ? (currentValue as { request_expected_status: number }).request_expected_status
                  : 200
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  request_expected_status: parseInt(e.target.value, 10) || undefined,
                });
              }}
              size="small"
              placeholder="200"
              helperText="Expected HTTP status code (default: 200)"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Variable Name (optional)"
              value={
                typeof currentValue === "object" && currentValue && "variable_name" in currentValue
                  ? Array.isArray((currentValue as { variable_name: string[] }).variable_name)
                    ? (currentValue as { variable_name: string[] }).variable_name[0]
                    : (currentValue as { variable_name: string }).variable_name
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  variable_name: e.target.value || undefined,
                });
              }}
              size="small"
              placeholder="api_response"
              helperText="Variable name to store response data"
            />
          </Stack>
        );

      case "api_check": {
        const checkId =
          typeof currentValue === "object" && currentValue && "check_id" in currentValue
            ? ((currentValue as { check_id?: string }).check_id ?? null)
            : null;
        return (
          <Stack spacing={2}>
            <CheckSelect
              label="API Check"
              appConfigId={appConfigId}
              value={checkId}
              size="small"
              onChange={(id) => {
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  check_id: id ?? undefined,
                });
              }}
              helperText="Select an existing API check to run as a step in this test"
            />
          </Stack>
        );
      }

      case "press":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Key or Key Combination"
            value={
              typeof currentValue === "object" && currentValue && "key" in currentValue
                ? (currentValue as { key: string }).key
                : typeof currentValue === "string"
                  ? currentValue
                  : ""
            }
            onChange={(e) => {
              onActionChange("value", { key: e.target.value });
            }}
            size="small"
            placeholder="Enter, Control+A, Shift+Tab"
            helperText="Single key (Enter, Escape) or combination (Control+A, Meta+Shift+K)"
          />
        );

      case "relative_drag_and_drop":
        return (
          <Stack spacing={2}>
            <FormControl fullWidth size="small" sx={textFieldSx}>
              <InputLabel>Drag Method</InputLabel>
              <Select
                value={
                  typeof currentValue === "object" && currentValue
                    ? "target_locator" in currentValue
                      ? "locator"
                      : "x" in currentValue || "y" in currentValue
                        ? "absolute"
                        : "delta_x" in currentValue || "delta_y" in currentValue
                          ? "relative"
                          : "relative"
                    : "relative"
                }
                onChange={(e) => {
                  const method = e.target.value;
                  if (method === "locator") {
                    onActionChange("value", { target_locator: "" });
                  } else if (method === "absolute") {
                    onActionChange("value", { x: 0, y: 0 });
                  } else {
                    onActionChange("value", { delta_x: 0, delta_y: 0 });
                  }
                }}
                label="Drag Method"
              >
                <MenuItem value="relative">Drag by Relative Offset (Recommended)</MenuItem>
                <MenuItem value="locator">Drag to Element</MenuItem>
                <MenuItem value="absolute">Drag to Absolute Position</MenuItem>
              </Select>
            </FormControl>

            {typeof currentValue === "object" &&
              currentValue &&
              "target_locator" in currentValue && (
                <TextField
                  sx={textFieldSx}
                  fullWidth
                  label="Target Element Selector"
                  value={(currentValue as { target_locator: string }).target_locator}
                  onChange={(e) => {
                    onActionChange("value", { target_locator: e.target.value });
                  }}
                  size="small"
                  placeholder=".drop-zone"
                  helperText="CSS selector of the target element to drop onto"
                />
              )}

            {typeof currentValue === "object" &&
              currentValue &&
              ("x" in currentValue || "y" in currentValue) &&
              !("delta_x" in currentValue) && (
                <Stack spacing={2}>
                  <TextField
                    sx={textFieldSx}
                    fullWidth
                    label="X Position (absolute)"
                    type="number"
                    value={(currentValue as { x?: number; y?: number }).x ?? ""}
                    onChange={(e) => {
                      const current =
                        typeof currentValue === "object" && currentValue
                          ? (currentValue as { x?: number; y?: number })
                          : {};
                      onActionChange("value", {
                        ...current,
                        x: parseInt(e.target.value, 10) || 0,
                      });
                    }}
                    size="small"
                    placeholder="300"
                    helperText="Absolute X coordinate on the page"
                  />
                  <TextField
                    sx={textFieldSx}
                    fullWidth
                    label="Y Position (absolute)"
                    type="number"
                    value={(currentValue as { x?: number; y?: number }).y ?? ""}
                    onChange={(e) => {
                      const current =
                        typeof currentValue === "object" && currentValue
                          ? (currentValue as { x?: number; y?: number })
                          : {};
                      onActionChange("value", {
                        ...current,
                        y: parseInt(e.target.value, 10) || 0,
                      });
                    }}
                    size="small"
                    placeholder="500"
                    helperText="Absolute Y coordinate on the page"
                  />
                </Stack>
              )}

            {typeof currentValue === "object" &&
              currentValue &&
              ("delta_x" in currentValue || "delta_y" in currentValue) && (
                <Stack spacing={2}>
                  <TextField
                    sx={textFieldSx}
                    fullWidth
                    label="X Offset (relative)"
                    type="number"
                    value={(currentValue as { delta_x?: number; delta_y?: number }).delta_x ?? ""}
                    onChange={(e) => {
                      const current =
                        typeof currentValue === "object" && currentValue
                          ? (currentValue as {
                              delta_x?: number;
                              delta_y?: number;
                            })
                          : {};
                      onActionChange("value", {
                        ...current,
                        delta_x: parseInt(e.target.value, 10) || 0,
                      });
                    }}
                    size="small"
                    placeholder="100"
                    helperText="Horizontal offset from source element (positive = right)"
                  />
                  <TextField
                    sx={textFieldSx}
                    fullWidth
                    label="Y Offset (relative)"
                    type="number"
                    value={(currentValue as { delta_x?: number; delta_y?: number }).delta_y ?? ""}
                    onChange={(e) => {
                      const current =
                        typeof currentValue === "object" && currentValue
                          ? (currentValue as {
                              delta_x?: number;
                              delta_y?: number;
                            })
                          : {};
                      onActionChange("value", {
                        ...current,
                        delta_y: parseInt(e.target.value, 10) || 0,
                      });
                    }}
                    size="small"
                    placeholder="200"
                    helperText="Vertical offset from source element (positive = down)"
                  />
                </Stack>
              )}
          </Stack>
        );

      case "reply_to_email":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Email ID"
              value={
                typeof currentValue === "object" && currentValue && "email_id" in currentValue
                  ? (currentValue as { email_id: string }).email_id
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  email_id: e.target.value,
                });
              }}
              size="small"
              placeholder="email_123456"
              helperText="ID of the email to reply to"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Reply Message"
              value={
                typeof currentValue === "object" && currentValue && "reply_body" in currentValue
                  ? (currentValue as { reply_body: string }).reply_body
                  : typeof currentValue === "string"
                    ? currentValue
                    : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  reply_body: e.target.value,
                });
              }}
              size="small"
              placeholder="Thank you for your email..."
              multiline
              minRows={3}
              maxRows={10}
              helperText="Reply message content"
            />
          </Stack>
        );

      case "scroll":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="X Delta (horizontal)"
              type="number"
              value={
                typeof currentValue === "object" && currentValue && "delta_x" in currentValue
                  ? (currentValue as { delta_x: number }).delta_x
                  : typeof currentValue === "object" && currentValue && "x" in currentValue
                    ? (currentValue as { x: number }).x
                    : ""
              }
              onChange={(e) => {
                const value = parseInt(e.target.value, 10) || 0;
                const current =
                  typeof currentValue === "object" && currentValue ? currentValue : {};
                onActionChange("value", {
                  ...current,
                  delta_x: value,
                  x: value,
                });
              }}
              size="small"
              placeholder="0"
              helperText="Horizontal scroll amount (positive = right)"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Y Delta (vertical)"
              type="number"
              value={
                typeof currentValue === "object" && currentValue && "delta_y" in currentValue
                  ? (currentValue as { delta_y: number }).delta_y
                  : typeof currentValue === "object" && currentValue && "y" in currentValue
                    ? (currentValue as { y: number }).y
                    : typeof currentValue === "number"
                      ? currentValue
                      : ""
              }
              onChange={(e) => {
                const value = parseInt(e.target.value, 10) || 0;
                if (value === 0) {
                  onActionChange("value", {
                    delta_y: value,
                    y: value,
                  });
                } else {
                  const current =
                    typeof currentValue === "object" && currentValue ? currentValue : {};
                  onActionChange("value", {
                    ...current,
                    delta_y: value,
                    y: value,
                  });
                }
              }}
              size="small"
              placeholder="500"
              helperText="Vertical scroll amount (positive = down)"
            />
          </Stack>
        );

      case "scroll_into_view_if_needed":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This action doesn't require additional parameters.
          </Typography>
        );

      case "select_option": {
        const isAiBasedSelect = action?.value_strategy === "ai_based";
        return (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                sx={{ ...textFieldSx, flex: 1 }}
                fullWidth
                label={isAiBasedSelect ? "Semantic Description" : "Option to Select"}
                value={
                  isAiBasedSelect
                    ? action?.value_semantic_text || ""
                    : typeof currentValue === "string"
                      ? currentValue
                      : ""
                }
                onChange={(e) => {
                  if (isAiBasedSelect) {
                    onActionChange("value_semantic_text", e.target.value);
                  } else {
                    onActionChange("value", e.target.value);
                  }
                }}
                size="small"
                placeholder={
                  isAiBasedSelect
                    ? "e.g., the first available option that is not disabled"
                    : "Option value or label to select"
                }
              />
              <Tooltip
                title="When enabled, AI will select the option at runtime based on your description, the current dropdown options, and available variables"
                arrow
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={isAiBasedSelect}
                      onChange={(e) => {
                        // Toggle the strategy - just set the strategy value
                        onActionChange("value_strategy", e.target.checked ? "ai_based" : undefined);
                      }}
                    />
                  }
                  label="AI Value"
                  sx={{ ml: 0, mr: 0 }}
                />
              </Tooltip>
            </Stack>
            {isAiBasedSelect && (
              <>
                <TextField
                  sx={textFieldSx}
                  fullWidth
                  label="Variable Name (optional)"
                  value={action?.value_variable_name || ""}
                  onChange={(e) =>
                    onActionChange("value_variable_name", e.target.value || undefined)
                  }
                  size="small"
                  placeholder="e.g., selected_option"
                  helperText="Name for storing the resolved value. Auto-generated if empty."
                />
                <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  AI will select the appropriate option at runtime based on the dropdown contents
                  and available variables.
                </Typography>
              </>
            )}
          </Stack>
        );
      }

      case "select_text":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This action doesn't require additional parameters.
          </Typography>
        );

      case "set_checked":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This action doesn't require additional parameters.
          </Typography>
        );

      case "set_input_files":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="File Path(s)"
            value={
              // Handle both direct string/array and object with file_paths/files
              typeof currentValue === "object" &&
              currentValue &&
              ("file_paths" in currentValue || "files" in currentValue)
                ? Array.isArray(
                    (
                      currentValue as {
                        file_paths?: string[];
                        files?: string[];
                      }
                    ).file_paths ||
                      (
                        currentValue as {
                          file_paths?: string[];
                          files?: string[];
                        }
                      ).files,
                  )
                  ? (
                      (
                        currentValue as {
                          file_paths?: string[];
                          files?: string[];
                        }
                      ).file_paths ||
                      (
                        currentValue as {
                          file_paths?: string[];
                          files?: string[];
                        }
                      ).files ||
                      []
                    ).join(", ")
                  : ""
                : Array.isArray(currentValue)
                  ? currentValue.join(", ")
                  : typeof currentValue === "string"
                    ? currentValue
                    : ""
            }
            onChange={(e) => {
              const paths = e.target.value
                .split(",")
                .map((p) => p.trim())
                .filter((p) => p);

              // For single file, use string directly; for multiple, use object with file_paths
              if (paths.length === 0) {
                onActionChange("value", "");
              } else if (paths.length === 1) {
                onActionChange("value", paths[0]);
              } else {
                onActionChange("value", { file_paths: paths });
              }
            }}
            size="small"
            placeholder="/path/to/file.txt"
            helperText="Comma-separated for multiple files"
          />
        );

      case "text_content":
      case "to_contain_text":
      case "to_have_text":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Expected Text"
            value={typeof currentValue === "string" ? currentValue : ""}
            onChange={(e) => onActionChange("value", e.target.value)}
            size="small"
            placeholder="Expected text content"
            multiline
            minRows={1}
            maxRows={5}
          />
        );

      case "to_be_checked":
      case "to_be_disabled":
      case "to_be_editable":
      case "to_be_empty":
      case "to_be_enabled":
      case "to_be_focused":
      case "to_be_hidden":
      case "to_be_unchecked":
      case "to_be_visible":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This assertion doesn't require additional parameters.
          </Typography>
        );

      case "to_have_url":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Expected URL"
            value={typeof currentValue === "string" ? currentValue : ""}
            onChange={(e) => onActionChange("value", e.target.value)}
            size="small"
            placeholder="https://example.com/page"
            helperText="The URL that the page should have"
          />
        );

      case "to_have_value":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Expected Value"
            value={typeof currentValue === "string" ? currentValue : ""}
            onChange={(e) => onActionChange("value", e.target.value)}
            size="small"
            placeholder="Expected input value"
          />
        );

      case "to_have_values":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Expected Values (comma-separated)"
            value={
              Array.isArray(currentValue)
                ? currentValue.join(", ")
                : typeof currentValue === "string"
                  ? currentValue
                  : ""
            }
            onChange={(e) => {
              const values = e.target.value
                .split(",")
                .map((v) => v.trim())
                .filter((v) => v);
              onActionChange("value", values.length === 1 ? values[0] : values);
            }}
            size="small"
            placeholder="value1, value2, value3"
            helperText="Multiple values separated by commas"
          />
        );

      case "uncheck":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This action doesn't require additional parameters.
          </Typography>
        );

      case "variable_json_path":
        return (
          <Stack spacing={2}>
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Source Variable Name"
              value={
                typeof currentValue === "object" && currentValue && "variable_name" in currentValue
                  ? Array.isArray((currentValue as { variable_name: string[] }).variable_name)
                    ? (currentValue as { variable_name: string[] }).variable_name[0] || ""
                    : (currentValue as { variable_name: string }).variable_name
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        variable_name?: string[];
                        json_path?: string;
                      })
                    : {};
                const outputVar =
                  Array.isArray(current.variable_name) && current.variable_name.length > 1
                    ? current.variable_name[1]
                    : "";
                onActionChange("value", {
                  ...current,
                  variable_name: [e.target.value, outputVar].filter(Boolean),
                });
              }}
              size="small"
              placeholder="input_variable"
              helperText="Name of variable containing JSON data"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="Output Variable Name"
              value={
                typeof currentValue === "object" && currentValue && "variable_name" in currentValue
                  ? Array.isArray((currentValue as { variable_name: string[] }).variable_name) &&
                    (currentValue as { variable_name: string[] }).variable_name.length > 1
                    ? (currentValue as { variable_name: string[] }).variable_name[1]
                    : ""
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        variable_name?: string[];
                        json_path?: string;
                      })
                    : {};
                const sourceVar =
                  Array.isArray(current.variable_name) && current.variable_name.length > 0
                    ? current.variable_name[0]
                    : "";
                onActionChange("value", {
                  ...current,
                  variable_name: [sourceVar, e.target.value].filter(Boolean),
                });
              }}
              size="small"
              placeholder="output_variable"
              helperText="Name of variable to store extracted value"
            />
            <TextField
              sx={textFieldSx}
              fullWidth
              label="JSON Path"
              value={
                typeof currentValue === "object" && currentValue && "json_path" in currentValue
                  ? (currentValue as { json_path: string }).json_path
                  : ""
              }
              onChange={(e) => {
                const current =
                  typeof currentValue === "object" && currentValue
                    ? (currentValue as {
                        variable_name?: string[];
                        json_path?: string;
                      })
                    : {};
                onActionChange("value", {
                  ...current,
                  json_path: e.target.value,
                });
              }}
              size="small"
              placeholder="$.data.items[0].name"
              helperText="JSONPath expression to extract value"
            />
          </Stack>
        );

      case "wait":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Wait Duration (ms)"
            type="number"
            value={typeof currentValue === "number" ? currentValue : ""}
            onChange={(e) => onActionChange("value", parseInt(e.target.value, 10) || undefined)}
            size="small"
            placeholder="5000"
            helperText="Wait duration in milliseconds"
          />
        );

      case "wait_for_load_state":
        return (
          <FormControl fullWidth size="small" sx={textFieldSx}>
            <InputLabel>Load State</InputLabel>
            <Select
              value={typeof currentValue === "string" ? currentValue : ""}
              onChange={(e) => onActionChange("value", e.target.value)}
              label="Load State"
            >
              <MenuItem value="load">Load (page loaded)</MenuItem>
              <MenuItem value="domcontentloaded">DOM Content Loaded</MenuItem>
              <MenuItem value="networkidle">Network Idle</MenuItem>
            </Select>
          </FormControl>
        );

      case "wait_for_selector":
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="CSS Selector"
            value={typeof currentValue === "string" ? currentValue : ""}
            onChange={(e) => onActionChange("value", e.target.value)}
            size="small"
            placeholder=".loading-spinner"
            helperText="Wait for this selector to appear"
          />
        );

      case "wait_for_timeout":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            Use the global timeout field below to set wait duration.
          </Typography>
        );

      case "wheel":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            This action doesn't require additional parameters.
          </Typography>
        );

      default:
        // Fallback for any unknown action types
        return (
          <TextField
            sx={textFieldSx}
            fullWidth
            label="Value"
            value={
              typeof currentValue === "string" ? currentValue : JSON.stringify(currentValue || "")
            }
            onChange={(e) => {
              let value: unknown = e.target.value;
              try {
                value = JSON.parse(e.target.value);
              } catch {
                // Keep as string if not valid JSON
              }
              onActionChange("value", value);
            }}
            size="small"
            placeholder="Enter value (JSON or string)"
            helperText="Generic value field for this action type"
          />
        );
    }
  };

  return renderActionFields();
};
